import { useAccount, useContractReads, useBalance as useEthBalance } from 'wagmi'
import { TOKENS as _TOKENS, Token, TokenBalance, TOKENS_EXCEPT_WETH } from '../tokens'
import { MULTICALL3_CONTRACT_ADDRESS, WETH_CONTRACT_ADDRESS, ZERO_ADDRESS } from '../constants'
import { useCallback, useMemo } from 'react'
import { Address, encodeFunctionData } from 'viem'
import { MULTICALL3_ABI } from '~/core/abis/multicall3.abi'

const ERC20_ABI = [
  {
    constant: true,
    inputs: [{ name: '_owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: 'balance', type: 'uint256' }],
    type: 'function',
  },
] as const

const TOKENS = Object.values(TOKENS_EXCEPT_WETH)

export function useAllBalance() {
  const { address } = useAccount()
  const { data: ethBalance, refetch: refetchEthBalance } = useEthBalance({ address })

  const calls = useMemo(() => {
    if (!address) return []
    return TOKENS.filter(token => token.address !== ZERO_ADDRESS).map(token => ({
      target: token.address as Address,
      callData: encodeFunctionData({
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [address],
      }),
    }))
  }, [address])

  const { data, isError, isLoading, refetch } = useContractReads({
    contracts: [
      {
        address: MULTICALL3_CONTRACT_ADDRESS,
        abi: MULTICALL3_ABI,
        functionName: 'aggregate',
        args: [calls],
      },
    ],
  })

  const initialBalances = useMemo(() => {
    return TOKENS.reduce((acc, token) => {
      acc[token.address] = new TokenBalance({
        token,
        balance: BigInt(0)
      });
      return acc;
    }, {} as Record<Address, TokenBalance>);
  }, []);

  const balances = useMemo(() => {
    if (!data || !data[0]) return null

    let tokenBalances: Record<Address, TokenBalance> = {
      [ZERO_ADDRESS]: new TokenBalance({
        token: _TOKENS[ZERO_ADDRESS],
        balance: ethBalance?.value || BigInt(0)
      })
    }

    tokenBalances = TOKENS.filter(token => token.address !== ZERO_ADDRESS).reduce((acc, token, index) => {
      const result = data[0].result ? data[0].result[1][index] : null
      acc[token.address] = new TokenBalance({
        token,
        balance: BigInt(result || 0)
      })
      return acc
    }, tokenBalances)

    return tokenBalances
  }, [data, ethBalance]) || initialBalances

  const getBalance = useCallback((tokenAddress: Address) => {
    if (tokenAddress === WETH_CONTRACT_ADDRESS)
      tokenAddress = ZERO_ADDRESS
    return balances[tokenAddress] || new TokenBalance({
      token: _TOKENS[tokenAddress],
      balance: BigInt(0)
    })
  }, [balances])

  const mutate = useCallback(async () => {
    await Promise.all([refetch(), refetchEthBalance()])
  }, [refetch, refetchEthBalance])

  return {
    balances,
    getBalance,
    isError,
    isLoading,
    mutate,
  }
}
